import { Directive, Input, OnChanges, OnDestroy, Optional } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  AsyncValidatorFn,
  NG_ASYNC_VALIDATORS,
  NG_VALIDATORS,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import { isEntity } from 'app/core/utils/entity-helper';
import { EntityInfo, FormValidators, GlobalValidationContext } from 'app/core/validation';
import { Observable, Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: 'form',
})
export class FormValidationDirective implements OnChanges {
  @Input() validationModel: any;
  @Input() validationModelType: string;
  @Input() validationGroup: string;
  @Input() validationContext: GlobalValidationContext;

  modelChange = new Subject<void>();

  ngOnChanges(): void {
    this.modelChange.next();
  }
}

@Directive()
abstract class FormFieldValidateDirectiveBase implements OnDestroy, OnChanges {
  @Input() validationModel: any;
  @Input() validationModelType: string;
  @Input() validationPropertyName: string;
  @Input() validationGroup: string;
  @Input() validationContext: GlobalValidationContext;

  protected modelChange = new Subject<void>();
  protected unsubscribeAll = new Subject<void>();

  get model(): any {
    return this.validationModel || this.formValidation?.validationModel;
  }

  get modelType(): string {
    return isEntity(this.model)
      ? this.model.entityType.shortName
      : this.validationModelType || this.formValidation?.validationModelType;
  }

  get group(): string {
    return this.validationGroup || this.formValidation?.validationGroup;
  }

  get context(): GlobalValidationContext {
    return this.validationContext || this.formValidation?.validationContext;
  }

  constructor(private formValidation: FormValidationDirective) {}

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  ngOnChanges(): void {
    this.modelChange.next();
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.unsubscribeAll.next();
    if (this.formValidation) {
      this.formValidation.modelChange.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => this.modelChange.next());
    }
    this.modelChange.pipe(takeUntil(this.unsubscribeAll)).subscribe(fn);
  }

  protected getModelInfo(): EntityInfo {
    if (!this.modelType) {
      throw new Error('Must provide validationModelType or validationModel must be an entity');
    }

    return {
      entity: this.model,
      entityType: this.modelType,
    };
  }
}

@Directive({
  selector: '[ngModel]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: FormFieldValidateDirective,
      multi: true,
    },
  ],
})
export class FormFieldValidateDirective extends FormFieldValidateDirectiveBase implements Validator, OnChanges {
  private validatorFn: ValidatorFn;

  constructor(private formValidators: FormValidators, @Optional() formValidation: FormValidationDirective) {
    super(formValidation);

    this.modelChange.subscribe(() => {
      this.validatorFn = this.formValidators.createSyncValidatorFn(
        () => this.getModelInfo(),
        this.validationPropertyName,
        this.group,
        this.context
      );
    });
  }

  validate(control: AbstractControl): ValidationErrors {
    if (!this.model || !this.validationPropertyName) {
      return null;
    }

    return this.validatorFn(control);
  }
}

@Directive({
  selector: '[ngModel]',
  providers: [
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: AsyncFormFieldValidateDirective,
      multi: true,
    },
  ],
})
export class AsyncFormFieldValidateDirective
  extends FormFieldValidateDirectiveBase
  implements AsyncValidator, OnChanges
{
  private validatorFn: AsyncValidatorFn;

  constructor(private formValidators: FormValidators, @Optional() formValidation: FormValidationDirective) {
    super(formValidation);

    this.modelChange.subscribe(() => {
      this.validatorFn = this.formValidators.createAsyncValidatorFn(
        () => this.getModelInfo(),
        this.validationPropertyName,
        this.group,
        this.context
      );
    });
  }

  validate(control: AbstractControl): Promise<ValidationErrors> | Observable<ValidationErrors> {
    if (!this.model || !this.validationPropertyName) {
      return of(null);
    }

    return this.validatorFn(control);
  }
}
